import {
  Typography,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Combobox,
} from "@altimateai/lego";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";
import { SubscriptionFormData } from "@/pages/subscriptions/services/types";
import { IconTrash } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { useMetricFilterOptions, useSearchableFilterOptions } from "../../services/wizardApi";
import { useMemo } from "react";

interface FilterRowProps {
  control: Control<SubscriptionFormData>;
  index: number;
  errors: FieldErrors<SubscriptionFormData>;
  onRemove: (index: number) => void;
  availableFilters?: string[];
  searchableFilters?: string[];
  metricName?: string;
}

const FilterRow = ({
  control,
  index,
  errors,
  onRemove,
  availableFilters = [],
  searchableFilters = [],
  metricName,
}: FilterRowProps) => {
  // Watch the dimension to fetch appropriate options
  const dimension = useWatch({ control, name: `filters.${index}.dimension` });

  const isSearchable = !!dimension && searchableFilters.includes(dimension);

  // Fetch filter options for non-searchable filters only
  const { data: filterOptions, isLoading: isLoadingOptions } = useMetricFilterOptions(
    isSearchable ? undefined : dimension,
    isSearchable ? undefined : metricName
  );

  // Searchable filter options (for high-cardinality filters like tags)
  const {
    options: searchableOptions,
    hasMore,
    loadMore,
  } = useSearchableFilterOptions(
    isSearchable ? dimension : undefined,
    isSearchable ? metricName : undefined
  );

  // Capitalize filter names for display
  const formatFilterName = (filter: string) => {
    return filter.charAt(0).toUpperCase() + filter.slice(1);
  };

  // Convert filter options to combobox format (only used for non-searchable)
  const comboboxOptions = useMemo(() => {
    if (!filterOptions?.options) return [];
    return filterOptions.options.map(opt => ({
      label: opt.label,
      value: opt.value,
    }));
  }, [filterOptions]);

  return (
    <div className="al-grid al-grid-cols-[auto_1fr_2fr_auto] al-gap-4 al-items-start">
      {/* Boolean operator column - only AND is allowed */}
      <div className="al-pt-2 al-min-w-[60px]">
        <Typography variant="body" className="al-text-gray-600">
          {index === 0 ? "Where" : "And"}
        </Typography>
      </div>

      {/* Dimension selector column */}
      <div>
        <Controller
          name={`filters.${index}.dimension`}
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                aria-label="Select dimension"
                className={cn(
                  "al-w-full al-text-start",
                  errors.filters?.[index]?.dimension ? "al-border-red-500" : ""
                )}
              >
                <SelectValue placeholder="Select dimension" />
              </SelectTrigger>
              <SelectContent>
                {availableFilters.length > 0 ? (
                  availableFilters.map(filter => (
                    <SelectItem key={filter} value={filter}>
                      {formatFilterName(filter)}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="" disabled>
                    No filters available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        />
        {errors.filters?.[index]?.dimension && (
          <Typography variant="body" size="xs" className="al-text-red-500 al-mt-1 al-block">
            {errors.filters[index]?.dimension?.message}
          </Typography>
        )}
      </div>

      {/* Value selector column */}
      <div>
        <Controller
          name={`filters.${index}.value`}
          control={control}
          render={({ field }) => (
            <Combobox
              options={isSearchable ? searchableOptions : comboboxOptions}
              value={field.value}
              onChange={newValue => field.onChange(newValue as string[])}
              placeholder={
                isLoadingOptions && !isSearchable
                  ? "Loading..."
                  : dimension
                    ? "Select values..."
                    : "Select dimension first"
              }
              multiSelect={true}
              onLoadMore={isSearchable ? loadMore : undefined}
              hasMore={isSearchable ? hasMore : undefined}
              buttonProps={{
                className: cn(
                  "al-w-full",
                  errors.filters?.[index]?.value ? "al-border-red-500" : ""
                ),
                disabled: !dimension || (isLoadingOptions && !isSearchable),
              }}
            />
          )}
        />
        {errors.filters?.[index]?.value && (
          <Typography variant="body" size="xs" className="al-text-red-500 al-mt-1 al-block">
            {errors.filters[index]?.value?.message}
          </Typography>
        )}
      </div>

      {/* Remove filter button column */}
      <div className="al-pt-1">
        <Button
          title="Remove filter"
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          className="al-text-gray-400 hover:al-text-red-500"
        >
          <IconTrash size={20} />
        </Button>
      </div>
    </div>
  );
};

export default FilterRow;
